import { queryKeys } from '@query/keys'
import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useAccountReadContext } from '@context/AccountContextContext'

import { SummaryItem } from '@ui/v2/TableSummary/TableSummary'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'

import { FilterQueryProps } from '@model/FilterFields'
import { expandTransactionStates } from '@model/TransactionStatus'

interface IBRebateTransactionsSummary {
  summaryItems: SummaryItem[]
  isLoading: boolean
}

export const useIBRebateTransactionsSummary = (
  search?: FilterQueryProps
): IBRebateTransactionsSummary => {
  const apiClient = useApiClient(ClientApiClient)
  const { account } = useAccountReadContext()
  const { t } = useTranslation()

  const ibId = account?.clientIntroducingBroker?.id

  const { data: summaryData, isLoading } = useQuery({
    queryKey: queryKeys.ib.rebates.transactionsSummary(ibId, search as Record<string, unknown>),
    queryFn: () =>
      apiClient.getIBRebateTransactionsSummary(ibId!, {
        search: expandTransactionStates(search),
      }),
    enabled: !!ibId,
  })

  const summaryItems = useMemo<SummaryItem[]>(() => {
    return [
      {
        title: t('IB.RebatesPage.Paid'),
        value: summaryData?.paid || 0,
      },
      {
        title: t('IB.RebatesPage.Pending'),
        value: summaryData?.pending || 0,
      },
      {
        title: t('IB.RebatesPage.Declined'),
        value: summaryData?.declined || 0,
      },
    ]
  }, [summaryData, t])

  return { summaryItems, isLoading }
}
